// src/pages/Profile.tsx
import React, { useState } from "react";
import { useUserStore } from "../../store/userStore";
import { userApi } from "../../apiServices/UserApiService";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const Profile: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const [showResetForm, setShowResetForm] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

   const basePath = process.env.REACT_APP_BASE_NAME || "";

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  if (!user) {
    return (
      <div className="container mt-5 text-center">
        <h4 className="text-danger">No user information found.</h4>
        <p>Please log in again.</p>
      </div>
    );
  }

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");

    // ✅ Validate password length
    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long.");
      return;
    }

    const oem = basePath.split("/")[1];

    try {
      const result = await userApi.resetPassword({
        email: user.emailId,
        oldPassword,
        newPassword,
        oem
      });

      setSnackbar({
        open: true,
        message: result.message || "✅ Password updated successfully!",
        severity: "success",
      });

      setOldPassword("");
      setNewPassword("");
      setShowResetForm(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "❌ Password update failed. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="fw-bold text-center mb-4">Profile</h2>

      <div className="card mx-auto shadow-sm" style={{ maxWidth: 450 }}>
        <div className="card-body text-center">
          {/* Avatar */}
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold mb-3"
            style={{ width: 70, height: 70, fontSize: 28 }}
          >
            {user.username?.substring(0, 1).toUpperCase()}
          </div>

          {/* User Info */}
          <h4 className="fw-semibold mb-1">{user.username}</h4>
          <p className="text-muted mb-1">{user.emailId}</p>
          <span
            className="badge bg-secondary text-uppercase"
            style={{ letterSpacing: "0.5px" }}
          >
            {user.role}
          </span>

          <hr className="my-4" />

          {/* Reset Password Toggle */}
          {!showResetForm ? (
            <button
              className="btn btn-outline-primary w-100"
              onClick={() => setShowResetForm(true)}
            >
              🔒 Reset Password
            </button>
          ) : (
            <form onSubmit={handlePasswordReset} className="text-start px-3">
              <div className="mb-3">
                <label className="form-label fw-semibold">Email ID</label>
                <input
                  type="email"
                  value={user.emailId}
                  className="form-control"
                  disabled
                />
              </div>

              {/* Old Password */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Old Password</label>
                <TextField
                  fullWidth
                  type={showOldPassword ? "text" : "password"}
                  value={oldPassword}
                  size="small"
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Enter old password"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowOldPassword((prev) => !prev)
                          }
                          edge="end"
                        >
                          {showOldPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              {/* New Password */}
              <div className="mb-3">
                <label className="form-label fw-semibold">New Password</label>
                <TextField
                  fullWidth
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  size="small"
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  error={!!passwordError}
                  helperText={passwordError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowNewPassword((prev) => !prev)
                          }
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary w-100">
                  🔄 Update Password
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetForm(false)}
                >
                  ❌
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Snackbar for alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Profile;
